import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { convertCamelToCapitalStart } from '@/common/utils/core';
import { getDisplayName } from '@/common/utils/core/string/displayName';
import { ASTER_KEY } from '@/dashboard/features/widgets/common/constants/define';
import {
  getV7TagsTargetTagValues,
  postV7TagsGlobalFilterTargetTagValues,
} from '@/api-client/metav6/queries/tag-controller/tag-controller';
import { TagRequest } from '@/api-client/metav6/model';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { SERVER_TAG_SEPARATOR } from '@/common/define/common.define';
import { TargetTagKind } from '../utils';

const DEFAULT_TAG = 'default_tag';

export const useTargetTagStore = defineStore('targetTagStore', () => {
  const TAG_MAP_KEY_CONNECTIVE = '//';
  const tagMap = ref<Map<string, string>>(new Map());
  const tagKindMap = ref<Map<string, TargetTagKind>>(new Map());

  const getTagKind = (tagKey: string): TargetTagKind =>
    (tagKindMap.value.get(tagKey) as TargetTagKind) || DEFAULT_TAG;

  const getTagMapKey = ({
    id = '',
    category = '',
    tagKind = DEFAULT_TAG,
  }: {
    id?: string;
    category?: string;
    tagKind?: TargetTagKind;
  }) =>
    `${id}${TAG_MAP_KEY_CONNECTIVE}${category}${TAG_MAP_KEY_CONNECTIVE}${
      tagKind === DEFAULT_TAG ? getTagKind(category) : tagKind
    }`;

  const getCategoryName = (category: string) =>
    convertCamelToCapitalStart(category?.replace(SERVER_TAG_SEPARATOR, '') ?? '');

  const getTagMapValue = ({
    id = '',
    category = '',
    tagKind = DEFAULT_TAG,
  }: {
    id?: string;
    category?: string;
    tagKind?: TargetTagKind;
  }) =>
    id === ASTER_KEY
      ? `${getCategoryName(category)}:${ASTER_KEY}`
      : (tagMap.value.get(getTagMapKey({ id, category, tagKind })) ?? '');

  const setTagMapValue = ({
    id = '',
    category = '',
    name = '',
    alias = '',
    tagKind = DEFAULT_TAG,
  }: {
    id?: string;
    category?: string;
    name?: string;
    alias?: string;
    tagKind?: TargetTagKind;
  }) => {
    tagMap.value.set(
      getTagMapKey({ id, category, tagKind }),
      `${getCategoryName(category)}:${getDisplayName(name, alias)}`,
    );
  };

  const setTagKindMap = ({
    category = '',
    tagKind = DEFAULT_TAG,
  }: {
    category?: string;
    tagKind?: TargetTagKind | string;
  }) => tagKindMap.value.set(category, tagKind as TargetTagKind);

  const fetchData = async (requests: TagRequest[]) => {
    try {
      const { data } = await postV7TagsGlobalFilterTargetTagValues(requests, undefined, {
        params: { frameName: FRAME_NAMES.TAG.TAG_ID_LIST },
      });
      if (data?.data?.length) {
        tagMap.value.clear();
        tagKindMap.value.clear();

        data.data.forEach((item) => {
          // @ts-expect-error TODO: FIXME
          setTagKindMap({ category: item.category!, tagKind: item.tagKind! });
          setTagMapValue({
            id: item.id,
            category: item.category,
            name: item.name,
            alias: item.alias,
            // @ts-expect-error TODO: FIXME
            tagKind: item.tagKind === 'user_tag' ? 'user_tag' : DEFAULT_TAG,
          });
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchDataByCategories = async (categories: string[], subCategory?: string) => {
    try {
      const { data } = await getV7TagsTargetTagValues(
        {
          tagKind: 'tag',
          categories,
          subCategory,
        },
        {
          params: {
            frameName: FRAME_NAMES.TAG.TARGET_TAGS_CATEGORY_BY_VALUE(categories.join(',')),
          },
        },
      );
      if (data?.data?.length) {
        tagMap.value.clear();
        tagKindMap.value.clear();

        data.data.forEach((item) => {
          const category = item.tagKey;
          setTagKindMap({ category, tagKind: item.tagKind! });
          item.tagValue?.forEach((v) => {
            setTagMapValue({
              id: v.id,
              category,
              name: v.name,
              alias: v.alias,
              tagKind: item.tagKind === 'user_tag' ? 'user_tag' : DEFAULT_TAG,
            });
          });
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchAllData = async (frameName?: string) => {
    try {
      const { data } = await getV7TagsTargetTagValues(
        {
          tagKind: 'tag',
        },
        {
          params: {
            frameName: frameName ?? FRAME_NAMES.TAG.TARGET_TAGS,
          },
        },
      );
      if (data?.data?.length) {
        tagMap.value.clear();
        tagKindMap.value.clear();

        data.data.forEach((item) => {
          const category = item.tagKey;
          setTagKindMap({ category, tagKind: item.tagKind! });
          item.tagValue?.forEach((v) => {
            setTagMapValue({
              id: v.id,
              category,
              name: v.name,
              alias: v.alias,
              tagKind: item.tagKind === 'user_tag' ? 'user_tag' : DEFAULT_TAG,
            });
          });
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  return {
    TAG_MAP_KEY_CONNECTIVE,
    tagMap,
    tagKindMap,
    fetchData,
    fetchAllData,
    fetchDataByCategories,
    getTagMapValue,
  };
});
